import { useState, useEffect } from 'react';
import AxiosInstance from 'src/components/Axios';

// Модульні кеші — живуть протягом сесії браузера.
// Скидаються вручну через invalidateSupportChannels() або при перезавантаженні сторінки.
let cacheActive = null;
let cacheInactive = null;

/**
 * Скидає обидва кеші каналів зв'язку (активні та неактивні).
 * Викликати після успішного створення або редагування каналу у SupportChannelsForm,
 * щоб наступні виклики хуків зробили свіжі запити на сервер.
 */
export const invalidateSupportChannels = () => {
	cacheActive = null;
	cacheInactive = null;
};

/**
 * Хук для отримання списку активних каналів зв'язку (`/support-channels?is_active=true`).
 *
 * Перший виклик робить HTTP-запит і зберігає результат у модульному кеші.
 * Усі наступні виклики (навіть у різних компонентах) повертають кешовані дані
 * без повторного запиту. Для примусового оновлення — викликати invalidateSupportChannels()
 * перед наступним рендером компонента, що використовує цей хук.
 *
 * @returns {Array} Масив об'єктів активних каналів. До завершення запиту
 *   повертає порожній масив (або вже кешовані дані, якщо кеш заповнений).
 *
 * @example
 * const channels = useSupportChannels();
 * // [{ id: 1, code: 'email', name: 'Email', is_active: true, sort_order: 0 }, ...]
 */
export const useSupportChannels = () => {
	const [items, setItems] = useState(cacheActive || []);

	useEffect(() => {
		if (cacheActive) return;
		AxiosInstance.get('/support-channels', { params: { is_active: true, itemsPerPage: 100 } })
			.then(res => {
				const list = Array.isArray(res.data) ? res.data : (res.data.data ?? []);
				cacheActive = list;
				setItems(list);
			})
			.catch(() => {});
	}, []);

	return items;
};

/**
 * Хук для отримання списку неактивних каналів зв'язку (`/support-channels?is_active=false`).
 *
 * Поведінка кешування аналогічна useSupportChannels — один запит на сесію.
 * Кеш спільно скидається через invalidateSupportChannels().
 *
 * Використовується для відображення застарілих (відключених) каналів,
 * що вже прив'язані до запитів підтримки.
 *
 * @returns {Array} Масив об'єктів неактивних каналів. До завершення запиту
 *   повертає порожній масив (або вже кешовані дані, якщо кеш заповнений).
 *
 * @example
 * const inactiveChannels = useInactiveSupportChannels();
 * // [{ id: 3, code: 'telegram', name: 'Telegram (старий)', is_active: false, sort_order: 99 }, ...]
 */
export const useInactiveSupportChannels = () => {
	const [items, setItems] = useState(cacheInactive || []);

	useEffect(() => {
		if (cacheInactive) return;
		AxiosInstance.get('/support-channels', { params: { is_active: false, itemsPerPage: 100 } })
			.then(res => {
				const list = Array.isArray(res.data) ? res.data : (res.data.data ?? []);
				cacheInactive = list;
				setItems(list);
			})
			.catch(() => {});
	}, []);

	return items;
};
